import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ImageUploader from "../components/ImageUploader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const inputCls = "bg-[#121212] border-axe-border rounded-xl text-white h-12 px-4";
const EMPTY = { name: "", logo: "", url: "", tier: "standard" };

export default function SponsorsEditor() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, ConfirmModal] = useConfirm();

  async function refresh() {
    const { data } = await api.get("/sponsors");
    setItems(data);
  }
  useEffect(() => {
    refresh();
  }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, logo: form.logo || "", url: form.url || "", tier: form.tier };
      if (form._editId) await api.put(`/sponsors/${form._editId}`, payload);
      else await api.post("/sponsors", payload);
      toast.success("Saved");
      setForm(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!(await confirm("Delete this sponsor?"))) return;
    try {
      await api.delete(`/sponsors/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="sponsors-editor">
      <ConfirmModal />
      <div className="flex justify-end mb-5">
        <button onClick={() => setForm({ ...EMPTY })} className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover" data-testid="sponsors-new">
          <Plus size={14} /> Add Sponsor
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((s) => (
          <div key={s.id} className="rounded-2xl border border-axe-border bg-axe-surface p-5" data-testid={`sponsors-row-${s.id}`}>
            <div className="h-20 flex items-center justify-center bg-axe-bg rounded-xl overflow-hidden">
              {s.logo ? (
                <img src={s.logo} alt={s.name} className="h-full w-auto object-contain px-4" />
              ) : (
                <div className="font-display font-black text-xl tracking-[0.15em] text-white">{s.name}</div>
              )}
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-axe-tangerine font-bold">{s.tier}</div>
              <div className="mt-1 font-display font-bold text-base text-white">{s.name}</div>
              {s.url && <div className="text-xs text-neutral-500 truncate mt-1">{s.url}</div>}
            </div>
            <div className="mt-4 flex gap-3">
              <button onClick={() => setForm({ ...s, _editId: s.id })} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`sponsors-edit-${s.id}`}>
                <Pencil size={12} /> Edit
              </button>
              <button onClick={() => remove(s.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`sponsors-delete-${s.id}`}>
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No sponsors yet.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="sponsors-modal">
          <form onSubmit={save} className="relative w-full max-w-xl bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 space-y-5 my-10">
            <button type="button" onClick={() => setForm(null)} className="absolute right-4 top-4 p-2 text-neutral-400 hover:text-white" aria-label="Close"><X size={18} /></button>
            <h3 className="font-display font-black text-2xl">{form._editId ? "Edit sponsor" : "New sponsor"}</h3>

            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="sponsors-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Logo</Label>
              <div className="mt-2 max-w-xs"><ImageUploader value={form.logo} onChange={(url) => setForm({ ...form, logo: url })} aspect="aspect-[4/3]" testId="sponsors-logo-uploader" label="Upload logo" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">URL</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="https://…" data-testid="sponsors-url" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Tier</Label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="sponsors-tier"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-axe-surface border-axe-border text-white">
                    {["platinum", "gold", "standard"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setForm(null)} className="rounded-full border border-axe-border px-6 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-bg">Cancel</button>
              <button type="submit" disabled={saving} className="rounded-full bg-axe-purple px-7 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover disabled:opacity-50" data-testid="sponsors-save">
                {saving ? "Saving…" : form._editId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
